// maybe put profile validators in a shared place for front and back end?
import _ from 'lodash';
import { RecruitmentActivityType } from '../../../../../shared/types/recruitmentTypes';
import { RecruitmentEntityType, RecruitmentScheduleType } from '../../../../../shared/types/recruitmentTypes';
import { TRecruitmentCharacterProfile, TRecruitmentGuildProfile, TRecruitmentProfile } from '../libs/store';

const customScheduleValidator = (p: any, errors: any[]) => {
  let isValid = true;

  for (const day of p.schedule) {
    const missingStart = _.isNil(day.start_minutes);
    const missingEnd = _.isNil(day.end_minutes);

    if (missingStart && missingEnd) {
      errors.push(`Day ${day.day_of_week + 1} missing both start and end times`);
      isValid = false;
    } else if (missingStart) {
      errors.push(`Day ${day.day_of_week + 1} missing start time`);
      isValid = false;
    } else if (missingEnd) {
      errors.push(`Day ${day.day_of_week + 1} missing end time`);
      isValid = false;
    }
  }

  return isValid;
};

// can add additional validations here, like comparing all schedule types against min commitment days.
// I thought to hold off on that maybe because it might be hard to explain that misconfiguration to a user.
const scheduleTypeValidation: { [key: string]: (p: TRecruitmentProfile, errors: any[]) => boolean } = {
  [RecruitmentScheduleType.Weekdays]: (p: any, errors: any[]) => {
    let isValid = true;
    if (p.schedule.length !== 5) {
      // FIXME: actually check if these are the proper days of the week
      errors.push('Must have configured your schedule on the weekdays');
      isValid = false;
    }
    if (!customScheduleValidator(p, errors)) {
      isValid = false;
    }
    return isValid;
  },

  [RecruitmentScheduleType.Weekends]: (p: any, errors: any[]) => {
    let isValid = true;
    if (p.schedule.length !== 2) {
      // FIXME: actually check if these are the proper days of the week
      errors.push('Must have configured your schedule on the weekends');
      isValid = false;
    }
    if (!customScheduleValidator(p, errors)) {
      isValid = false;
    }
    return isValid;
  },

  [RecruitmentScheduleType.Custom]: customScheduleValidator,

  [RecruitmentScheduleType.Any]: (p: any, errors: any[]) => {
    let isValid = true;
    if (p.schedule.length !== 7) {
      errors.push('Must have configured every day in the week');
      isValid = false;
    }
    if (!customScheduleValidator(p, errors)) {
      isValid = false;
    }
    return isValid;
  }
};

const helpers: any = {
  /*
   *
   * HELPER VALIDATORS
   *
   *
   */
  characterHasMainSpec: (char: any) =>
    _.some(_.entries(char), ([key, value]) => key.match('mainrole_') && _.isString(value)),

  commonSchedule: (p: any, errors: any[]) => {
    let isValid = true;

    if (_.isEmpty(p.schedule_timezone) || p.schedule_timezone === 'UTC') {
      errors.push('No timezone is configured');
      isValid = false;
    }

    if (p.scheduleType === '') {
      errors.push('Missing schedule type');
      isValid = false;
    }

    if (p.commitment_days_min < 1 || p.commitment_days_min > 7) {
      // they must have configured at least the minimum number of days in their schedule
      errors.push('Minimum commitment days out of range (min: 1, max: 7)');
      isValid = false;
    }

    if (p.commitment_days_max < 1 || p.commitment_days_max > 7) {
      // they must have configured at least the minimum number of days in their schedule
      errors.push('Maximum commitment days out of range (min: 1, max: 7)');
      isValid = false;
    }

    if (p.commitment_hours_min < 1 || p.commitment_hours_min > 24) {
      // they must have configured at least the minimum number of days in their schedule
      errors.push('Minimum commitment hours out of range (min: 1, max: 24)');
      isValid = false;
    }

    if (p.commitment_hours_max < 1 || p.commitment_hours_max > 24) {
      // they must have configured at least the minimum number of days in their schedule
      errors.push('Maximum commitment hours out of range (min: 1, max: 24)');
      isValid = false;
    }

    if (p.schedule.length < p.commitment_days_min) {
      // they must have configured at least the minimum number of days in their schedule
      errors.push(`Schedule must have at least ${p.commitment_days_min} days (your minimum commitment)`);
      isValid = false;
    }

    return isValid;
  },

  characterSchedule: (p: any, errors: any[]) => {
    let isValid = true;

    if (!helpers.commonSchedule(p, errors)) {
      isValid = false;
    }

    const scheduleValidator = scheduleTypeValidation[p.scheduleType];
    if (scheduleValidator) {
      if (!scheduleValidator(p, errors)) {
        isValid = false;
      }
    } else {
      errors.push('Schedule is not configured');
      isValid = false;
    }

    return isValid;
  },

  guildSchedule: (p: any, errors: any[]) => {
    let isValid = true;

    if (!helpers.commonSchedule(p, errors)) {
      isValid = false;
    }

    const scheduleValidator = scheduleTypeValidation[p.scheduleType];
    if (scheduleValidator) {
      if (!scheduleValidator(p, errors)) {
        isValid = false;
      }
    } else {
      errors.push('Invalid schedule');
      isValid = false;
    }

    return isValid;
  }
  /*
   * END HELPER VALIDATORS
   */
};

// validators for portions of a profile. Used for step selection/limitation/display,
// publish/activate/renew restrictions
export const profileStepValidators: { [key: string]: (p: TRecruitmentProfile, errors: any[]) => boolean } = {
  /*
   *
   * MAIN CATEGORY BASED METHODS
   *
   * */
  requirements: (p: any, errors: any[]) => {
    let isValid = true;

    const askAbout = getRequiredTransferRequirements(p.entity_type as any, p.activity_type as any);
    // if (askAbout.crossRealm && typeof p.is_cross_realm_allowed !== 'boolean') {
    //   errors.push(`Must provide answer to cross-realm/cross-faction requirement`);
    //   isValid = false;
    // }

    if (askAbout.realmTransfer && typeof p.is_realm_transfer_allowed !== 'boolean') {
      errors.push('Must provide answer to realm transfer requirement');
      isValid = false;
    }

    // if (askAbout.factionTransfer && typeof p.is_faction_transfer_allowed !== 'boolean') {
    //   errors.push(`Must provide answer to faction transfer requirement`);
    //   isValid = false;
    // }

    // ensure there's no unconfigured requirements
    (p.requirements || []).forEach((req: any, index: number) => {
      if (typeof req.min_value !== 'number') {
        errors.push(`Requirement #${index + 1} does not have a value set (ID ${req.id}) `);
        isValid = false;
      }

      if (req.min_value < 0) {
        errors.push(`Requirement #${index + 1} cannot be negative`);
        isValid = false;
      }

      if (typeof req.max_value === 'number' && req.max_value < req.min_value) {
        errors.push(`Requirement #${index + 1} max value cannot be less than min value`);
        isValid = false;
      }
    });

    return isValid;
  },

  schedule: (p: any, errors: any[]) => {
    if (p.entity_type === RecruitmentEntityType.Character) {
      return helpers.characterSchedule(p, errors);
    } else {
      return helpers.guildSchedule(p, errors);
    }
  },

  // There must be at least 1 configured language
  publish: (p: any, errors: any[]) => {
    const languageCount = _.compact([p.language_0, p.language_1, p.language_2]).length;
    if (languageCount === 0) {
      errors.push('Must choose at least one language');
      return false;
    }
    return true;
  },

  characterSlots: (p: any, errors: any[]) => {
    if (_.isEmpty(p.characters)) {
      errors.push('Must have at least one character');
      return false;
    }

    if (!_.find(p.characters, { ordinal: 0 })) {
      errors.push('Could not find Character Slot for Main Character');
      return false;
    }

    let isValid = true;

    p.characters.forEach((ch: any, index: number) => {
      if (!helpers.characterHasMainSpec(ch)) {
        errors.push(`Slot ${index + 1} is missing Main Spec selection`);
        isValid = false;
      }
    });

    return isValid;
  },

  // keeping this below for reference, my sheet says no unspecced alts, but wasn't sure
  // if we would want to allow an alt with only an offspec selected.
  // characterSlots: (p: TRecruitmentCharacterProfile) => {
  // const { main, alts} = _.groupBy(p.characters, (c) => c.ordinal === 0 ? 'main' : 'alts');
  // if (_.isEmpty(main)) {
  // return false;
  // }
  // const mainHasSpec  = characterHasMainSpec(main[0]);

  // const unspeccedAlts = _.some(alts, (a) => !characterHasMainSpec(a));

  // return mainHasSpec && !unspeccedAlts;
  // },

  // must have at least 1 valid slot
  // A slot is considered valid if:
  //  - role type
  //  - OR class type and either main or offspect selected
  playerSlots: (p: any, errors: any[]) => {
    if (_.isEmpty(p.slots)) {
      errors.push('You must have at least one Player Slot');
      return false;
    }

    let validSlotCount = 0;
    let isValid = true;

    p.slots.forEach((slot: any, slotIndex: number) => {
      for (const entry of slot.entries) {
        if (entry.type === 'class') {
          // for class type slots, there must be at least 1 spec selection
          const roleSelectedForEntry = _.some(
            _.entries(entry),
            ([key, value]) => key.match('role_') && _.isString(value)
          );
          if (!roleSelectedForEntry) {
            errors.push(`Slot ${slotIndex + 1} has class (${entry.class_id}) with no specs selected`);
            isValid = false;
          }
          ++validSlotCount;
        } else {
          // unsupported type
          errors.push(`Slot ${slotIndex + 1} has unsupported slot type (${entry.type})`);
          isValid = false;
        }
      }
    });

    if (validSlotCount === 0) {
      errors.push('You must have at least one valid Player Slot');
      isValid = false;
    }

    return isValid;
  }
};

const baseProfileValidation = (profile: TRecruitmentProfile, errors: any[]) =>
  _.every([
    profileStepValidators.requirements(profile, errors),
    profileStepValidators.publish(profile, errors),
    profileStepValidators.schedule(profile, errors)
  ]);

// per-entity_type validation
// used on RecruitmentProfileCard to determine whether or not a profile can be activated/renewed
const checkProfileValidity: { [key: string]: (p: any, errors: any[]) => boolean } = {
  [RecruitmentEntityType.Character]: (profile: TRecruitmentCharacterProfile, errors: any[]) =>
    _.every([baseProfileValidation(profile, errors), profileStepValidators.characterSlots(profile, errors)]),

  [RecruitmentEntityType.Guild]: (profile: TRecruitmentGuildProfile, errors: any[]) =>
    _.every([baseProfileValidation(profile, errors), profileStepValidators.playerSlots(profile, errors)])
};

/**
 * Returns whether a profile is complete.
 *
 * Optionally pass an array to get the specific errors that occurred.
 */
export const isProfileComplete = (profile: TRecruitmentProfile | undefined | null, errors: any[] = []) => {
  if (!profile) {
    return false;
  }
  const validator = checkProfileValidity[profile.entity_type];
  if (!validator) {
    errors.push(`Unsupported profile entity ${profile.entity_type}`);
    return false;
  }
  return validator(profile, errors);
};

// this indicates the additional fields they need to be setting
export function getRequiredTransferRequirements(
  entityType: RecruitmentEntityType,
  activityType: RecruitmentActivityType
) {
  const isGuildActivity = [
    RecruitmentActivityType.GuildPvp,
    RecruitmentActivityType.GuildRaids,
    RecruitmentActivityType.GuildSocial
  ].includes(activityType);

  const realmTransfer = entityType === RecruitmentEntityType.Character && isGuildActivity;
  const factionTransfer = entityType === RecruitmentEntityType.Character && isGuildActivity;
  const crossRealm = false;

  return {
    any: realmTransfer || factionTransfer || crossRealm,
    realmTransfer,
    factionTransfer,
    crossRealm
  };
}
